import { FC } from 'react';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import Button from 'common/components/buttons/button';

export enum IntegrationStatus {
  ENABLED = 'enabled',
  AUTHENTICATED = 'authenticated',
  ERROR = 'error',
  NOT_AUTHENTICATED = 'not_authenticated',
  DISABLED = 'disabled',
  COMING_SOON = 'contact customer support',
  NOT_CONFIGURED = ''
}

interface IntegrationTileProps {
  name: string;
  status: IntegrationStatus;
  logo: string;
  onAction?: () => void;
}

const statusStyles: Record<IntegrationStatus, string> = {
  [IntegrationStatus.ENABLED]:
    'border-neutral-200 shadow-lg shadow-secondary-200/10',
  [IntegrationStatus.AUTHENTICATED]:
    'border-neutral-200 shadow-lg shadow-tertiary-400/10',
  [IntegrationStatus.ERROR]: 'border-neutral-200 shadow-lg shadow-error-300/10',
  [IntegrationStatus.NOT_AUTHENTICATED]:
    'border-neutral-200 shadow-lg shadow-secondary-200/10',
  [IntegrationStatus.DISABLED]: 'bg-neutral-100 border-neutral-200',
  [IntegrationStatus.COMING_SOON]:
    'bg-neutral-100 border-neutral-200 opacity-50',
  [IntegrationStatus.NOT_CONFIGURED]:
    'border-neutral-200 shadow-lg shadow-secondary-200/10'
};

const authStatusStyles: Record<
  IntegrationStatus,
  { icon: string; textColor: string }
> = {
  [IntegrationStatus.AUTHENTICATED]: {
    icon: 'ra-icons-generic-check',
    textColor: 'text-success-400'
  },
  [IntegrationStatus.NOT_AUTHENTICATED]: {
    icon: 'ra-icons-generic-cross',
    textColor: 'text-error-300'
  },
  [IntegrationStatus.COMING_SOON]: {
    icon: '',
    textColor: 'text-neutral-500'
  },
  [IntegrationStatus.ENABLED]: {
    icon: '',
    textColor: 'text-primary-700'
  },
  [IntegrationStatus.ERROR]: {
    icon: 'ra-icons-generic-warning-2',
    textColor: 'text-error-300'
  },
  [IntegrationStatus.DISABLED]: {
    icon: 'ra-icons-generic-cross',
    textColor: 'text-neutral-500'
  },
  [IntegrationStatus.NOT_CONFIGURED]: {
    icon: '',
    textColor: 'text-neutral-500'
  }
};

const IntegrationTile: FC<IntegrationTileProps> = ({
  name,
  status,
  logo,
  onAction
}) => {
  let buttonTheme: ButtonTheme = ButtonTheme.PRIMARY;

  if (status === IntegrationStatus.AUTHENTICATED) {
    buttonTheme = ButtonTheme.SECONDARY;
  } else if (status === IntegrationStatus.ERROR) {
    buttonTheme = ButtonTheme.SUCCESS;
  } else if (status === IntegrationStatus.NOT_AUTHENTICATED) {
    buttonTheme = ButtonTheme.SUCCESS;
  } else if (status === IntegrationStatus.DISABLED) {
    buttonTheme = ButtonTheme.SUCCESS;
  } else if (status === IntegrationStatus.NOT_CONFIGURED) {
    buttonTheme = ButtonTheme.SUCCESS;
  }

  return (
    <div
      className={`border rounded-lg p-4 shadow-sm ${statusStyles[status]} text-center h-[140px] w-[250px]`}
    >
      <div className="flex items-center justify-center space-x-3">
        <img src={logo} alt={`${name} logo`} className="w-8 h-8" />
        <h3 className="text-lg font-semibold">{name}</h3>
      </div>

      <div className="flex items-center justify-center space-x-2 mt-2">
        {authStatusStyles[status] && (
          <i
            className={`text-sm ${authStatusStyles[status].icon} ${authStatusStyles[status].textColor}`}
          />
        )}
        <p className="text-sm capitalize">{status.replace('_', ' ')}</p>
      </div>

      {status !== IntegrationStatus.COMING_SOON && (
        <Button
          theme={buttonTheme}
          identifier={`btn-${name.toLowerCase().replace(' ', '-')}`}
          onClick={onAction ?? (() => {})}
          classOverrides="mt-3 px-4 py-2 rounded text-white text-xs"
        >
          <span>
            {status === IntegrationStatus.AUTHENTICATED
              ? 'Disable'
              : status === IntegrationStatus.NOT_AUTHENTICATED
              ? 'Authenticate'
              : status === IntegrationStatus.ERROR
              ? 'Re-authenticate'
              : 'Enable'}
          </span>
        </Button>
      )}
    </div>
  );
};

export default IntegrationTile;
